import {
  createContext,
  useContext,
  useReducer,
  useMemo,
  useEffect,
  ReactNode,
  Dispatch,
} from "react";

// The Soft UI Dashboard PRO Material main context
const SoftUI = createContext<any>(null);

// Setting custom name for the context which is visible on react dev tools
SoftUI.displayName = "SoftUIContext";

/** Clé localStorage dédiée — ne pas inclure dans le nettoyage à la déconnexion */
export const THEME_STORAGE_KEY = "waaconnect-color-mode";

export type ColorMode = "light" | "dark";

function readStoredColorMode(): ColorMode {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "dark" || stored === "light") {
      return stored;
    }
  } catch {
    // localStorage indisponible (mode privé, etc.)
  }
  return "light";
}

function applyColorModeToDocument(colorMode: ColorMode) {
  document.documentElement.setAttribute("data-color-mode", colorMode);
  document.documentElement.classList.toggle("dark", colorMode === "dark");
}

interface State {
  miniSidenav: boolean;
  transparentSidenav: boolean;
  sidenavColor: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark";
  transparentNavbar: boolean;
  fixedNavbar: boolean;
  openConfigurator: boolean;
  direction: "ltr" | "rtl";
  layout: "dashboard" | "page";
  colorMode: ColorMode;
}

interface Action {
  type: string;
  value: any;
}

// Soft UI Dashboard React reducer
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "MINI_SIDENAV": {
      return { ...state, miniSidenav: action.value };
    }
    case "TRANSPARENT_SIDENAV": {
      return { ...state, transparentSidenav: action.value };
    }
    case "SIDENAV_COLOR": {
      return { ...state, sidenavColor: action.value };
    }
    case "TRANSPARENT_NAVBAR": {
      return { ...state, transparentNavbar: action.value };
    }
    case "FIXED_NAVBAR": {
      return { ...state, fixedNavbar: action.value };
    }
    case "OPEN_CONFIGURATOR": {
      return { ...state, openConfigurator: action.value };
    }
    case "DIRECTION": {
      return { ...state, direction: action.value };
    }
    case "LAYOUT": {
      return { ...state, layout: action.value };
    }
    case "COLOR_MODE": {
      return { ...state, colorMode: action.value };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// Soft UI Dashboard React context provider
function SoftUIControllerProvider({ children }: { children: ReactNode }) {
  const initialState: State = {
    miniSidenav: false,
    transparentSidenav: false,
    sidenavColor: "success",
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
    colorMode: readStoredColorMode(),
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    applyColorModeToDocument(controller.colorMode);
  }, [controller.colorMode]);

  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

  return <SoftUI.Provider value={value}>{children}</SoftUI.Provider>;
}

// Soft UI Dashboard React custom hook for using context
function useSoftUIController() {
  const context = useContext(SoftUI);

  if (!context) {
    throw new Error("useSoftUIController should be used inside the SoftUIControllerProvider.");
  }

  return context;
}

// Context module functions
const setMiniSidenav = (dispatch: Dispatch<Action>, value: boolean) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch: Dispatch<Action>, value: boolean) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setSidenavColor = (dispatch: Dispatch<Action>, value: string) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch: Dispatch<Action>, value: boolean) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch: Dispatch<Action>, value: boolean) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch: Dispatch<Action>, value: boolean) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch: Dispatch<Action>, value: string) => dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch: Dispatch<Action>, value: string) => dispatch({ type: "LAYOUT", value });

const setColorMode = (dispatch: Dispatch<Action>, value: ColorMode) => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, value);
  } catch {
    // ignore
  }
  applyColorModeToDocument(value);
  dispatch({ type: "COLOR_MODE", value });
};

const toggleColorMode = (dispatch: Dispatch<Action>, current: ColorMode) =>
  setColorMode(dispatch, current === "light" ? "dark" : "light");

export {
  SoftUIControllerProvider,
  useSoftUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setColorMode,
  toggleColorMode,
};
